/**
 * Organizations list – same listing UI as Customers/Contacts (Card, DataTable, search, Pagination).
 */
import React, { useState, useEffect } from 'react';
import { useRestorableState, useScrollMemory } from '../hooks/useListMemory';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchInput } from '../components/ui/SearchInput';
import { PageLayout } from '../components/layout/PageLayout';
import { DataTable } from '../components/ui/DataTable';
import { Pagination } from '../components/ui/Pagination';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { useApp } from '../App';
import { useAppSelector } from '../store/hooks';
import { selectHasPermission } from '../store/slices/authSlice';
import { marketingAPI, Organization, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../lib/marketing-api';
import { NavLink } from 'react-router-dom';
import { Users, UserCircle, Search, Plus, Edit, Trash2, Building2, IdCard } from 'lucide-react';
import { cn } from '../lib/utils';
import { Tooltip } from '../UI/Tooltip';

export const OrganizationsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useApp();
  const underDatabase = location.pathname.startsWith('/database');
  const canView = useAppSelector(selectHasPermission('marketing.view_organization'));
  const canCreate = useAppSelector(selectHasPermission('marketing.create_organization'));
  const canEdit = useAppSelector(selectHasPermission('marketing.edit_organization'));
  const canDelete = useAppSelector(selectHasPermission('marketing.delete_organization'));

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useRestorableState('organizations:page', 1);
  const [pageSize, setPageSize] = useRestorableState('organizations:pageSize', DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  useScrollMemory('organizations', !isLoading);
  const [searchTerm, setSearchTerm] = useRestorableState('organizations:searchTerm', '');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const res = await marketingAPI.getOrganizations({
        page,
        page_size: pageSize,
        search: searchTerm || undefined,
      });
      setOrganizations(res.items);
      setTotal(res.total);
      setTotalPages(res.total_pages);
    } catch (e: any) {
      showToast(e.message || 'Failed to load organizations', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!canView) return;
    loadData();
  }, [canView, page, pageSize, searchTerm]);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  const handleDelete = async () => {
    if (deleteId == null) return;
    try {
      await marketingAPI.deleteOrganization(deleteId);
      showToast('Organization deleted', 'success');
      setDeleteId(null);
      loadData();
    } catch (e: any) {
      showToast(e.message || 'Failed to delete organization', 'error');
    }
  };

  const actions = canCreate ? (
    <Button size="sm" onClick={() => navigate('/organizations/new')} leftIcon={<Plus size={14} strokeWidth={3} />}>
      Add Organization
    </Button>
  ) : undefined;

  const breadcrumbs = underDatabase
    ? [{ label: 'Database', href: '/database' }, { label: 'Organizations', href: '/database/organizations' }]
    : [{ label: 'Organizations', href: '/organizations' }];

  if (!canView) {
    return (
      <PageLayout
        title="Organizations"
        breadcrumbs={breadcrumbs}
        description="Manage organizations and their plants. Select an organization when adding a customer or contact."
      >
        <Card>
          <div className="text-center py-12">
            <p className="text-slate-600">You do not have permission to view organizations.</p>
            <p className="text-sm text-slate-500 mt-2">Required permission: marketing.view_organization</p>
          </div>
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Organizations"
      breadcrumbs={breadcrumbs}
      description="Manage organizations and their plants. Select an organization when adding a customer or contact."
      actions={actions}
    >
      {/* Consolidated Command Bar */}
      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm px-5 py-0 h-14 mb-4 flex items-center">
        <div className="flex items-center justify-between gap-6 w-full h-full flex-wrap lg:flex-nowrap">
          {/* Internal Navigation group */}
          <div className="flex items-center h-full">
            <nav className="flex gap-6 h-full">
              {[
                { path: '/database/organizations', label: 'Organizations', icon: Building2, permission: 'marketing.view_organization' },
                { path: '/database/customers', label: 'Customers', icon: Users, permission: 'marketing.view_customer' },
                { path: '/database/contacts', label: 'Contacts', icon: UserCircle, permission: 'marketing.view_contact' },
                { path: '/database/visiting-card-contacts', label: 'Visiting Cards', icon: IdCard, permission: 'marketing.view_visiting_card_contact' },
              ].map((tab) => (
                <NavLink
                  key={tab.path}
                  to={tab.path}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 h-full px-1 text-sm font-bold transition-all border-b-2 -mb-[1px] relative z-10',
                      isActive
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-slate-400 hover:text-slate-600'
                    )
                  }
                >
                  <tab.icon size={14} strokeWidth={2.5} />
                  <span className="uppercase tracking-widest text-[11px] whitespace-nowrap">{tab.label}</span>
                </NavLink>
              ))}
            </nav>
          </div>

          {/* Centered Vertical Divider */}
          <div className="hidden lg:block w-[1px] h-5 bg-slate-200" />

          {/* Action group: search */}
          <div className="flex flex-1 items-center gap-3">
            <SearchInput
              placeholder="Search organizations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => setSearchTerm('')}
              containerClassName="max-w-md shadow-none"
            />
          </div>
        </div>
      </div>

      <Card noPadding contentClassName="py-0" className="overflow-hidden">
        {isLoading ? (
          <div className="py-24 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            <p className="mt-4 text-slate-600">Loading organizations...</p>
          </div>
        ) : organizations.length === 0 ? (
          <div className="py-24 text-center">
            <Building2 className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-900 font-semibold">No organizations found</p>
            <p className="text-slate-500 text-sm mt-2">Add an organization or adjust your search.</p>
            {canCreate && (
              <Button className="mt-4" size="sm" onClick={() => navigate('/organizations/new')} leftIcon={<Plus size={14} />}>
                Add Organization
              </Button>
            )}
          </div>
        ) : (
          <>
            <DataTable<Organization>
              bordered={false}
              data={organizations}
              rowKey={(o) => o.id}
              onRowClick={canEdit ? (o) => navigate(`/organizations/${o.id}/edit`) : undefined}
              dense={true}
              showVerticalLines={true}
              columns={[
                {
                  key: 'name',
                  label: 'Organization',
                  render: (o) => (
                    <div>
                      <div className="font-medium text-slate-900">{o.name}</div>
                      {o.code && <div className="text-xs text-slate-500 mt-0.5">{o.code}</div>}
                    </div>
                  ),
                },
                {
                  key: 'description',
                  label: 'Details',
                  render: (o) => (
                    <div className="text-sm text-slate-600 max-w-xs truncate" title={o.description || undefined}>
                      {o.description || '—'}
                    </div>
                  ),
                },
                {
                  key: 'industry',
                  label: 'Industry',
                  render: (o) => <span className="text-sm text-slate-600">{o.industry || '—'}</span>,
                },
                {
                  key: 'is_active',
                  label: 'Status',
                  render: (o) =>
                    o.is_active ? (
                      <span className="text-xs font-medium text-emerald-600">Active</span>
                    ) : (
                      <span className="text-xs font-medium text-slate-400">Inactive</span>
                    ),
                },
                {
                  key: 'actions',
                  label: '',
                  sortable: false,
                  align: 'right',
                  render: (o) => (
                    <div className="flex items-center justify-end gap-1">
                      {canEdit && (
                        <Tooltip content="Edit Organization">
                          <Button
                            variant="ghost"
                            size="xs"
                            className="w-8 h-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-transparent transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/organizations/${o.id}/edit`);
                            }}
                          >
                            <Edit size={16} />
                          </Button>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip content="Delete Organization">
                          <Button
                            variant="ghost"
                            size="xs"
                            className="w-8 h-8 p-0 text-rose-500 hover:text-rose-600 hover:bg-transparent transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteId(o.id);
                            }}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  ),
                },
              ]}
            />
            <div className="border-t border-slate-200 px-4 py-3">
              <Pagination
                page={page}
                pageSize={pageSize}
                total={total}
                totalPages={totalPages}
                onPageChange={setPage}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
              />
            </div>
          </>
        )}
      </Card>
      <ConfirmModal
        isOpen={deleteId != null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete organization"
        message="Are you sure? This will also remove all plants under this organization."
      />
    </PageLayout>
  );
};
